import React, { useState, useRef } from "react";
import { APP_LOGO_PATH } from '../../../shared/settings/config';
import './UserAgreement.scss';

const TOTAL_PAGES = 3;

export default function UserAgreement(props) {
    const [menuOpen, setMenuOpen] = useState(false);
    const [page, setPage] = useState(0);
    const agreementBodyRef = useRef(null);

    const closeMenu = () => setMenuOpen(false);

    const goToPage = (nextPage) => {
        if (nextPage < 0 || nextPage >= TOTAL_PAGES) return;
        setPage(nextPage);
        if (agreementBodyRef.current) {
            agreementBodyRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const progressPercent = ((page + 1) / TOTAL_PAGES) * 100;

    const handleNext = () => {
        if (page === TOTAL_PAGES - 1) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            setPage(0);
        } else {
            goToPage(page + 1);
        }
    };

    const handleBack = () => {
        goToPage(page - 1);
    };

    return (
        <div className="user-agreement-main-content">
            <nav className={`navbar navbar-expand-md px-3 agreement-navbar ${menuOpen ? "menu-open" : ""}`}>
                <div className="d-flex justify-content-sm-between max-container">

                    <img className="w-8rem" src={APP_LOGO_PATH} alt="Logo" />

                    <button className="navbar-toggler" type="button" onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle navigation">
                        <span className="navbar-toggler-icon"></span>
                    </button>

                    <div className="collapse navbar-collapse" id="navMenu">
                        <ul className="navbar-nav ms-auto align-items-lg-center gap-lg-3 clickable">
                            <li className="nav-item fw-bold">
                                <a className="nav-link" href="/#features" onClick={closeMenu}>Features</a>
                            </li>
                            <li className="nav-item fw-bold">
                                <a className="nav-link" href="/#pricing" onClick={closeMenu}>Pricing</a>
                            </li>
                            <li className="nav-item fw-bold">
                                <a className="nav-link" href="/sign-in" onClick={closeMenu}>Login</a>
                            </li>
                            <li className="nav-item fw-bold">
                                <a className="btn btn-sm btn-light rounded-pill px-3 mt-2 mt-lg-0" href="/sign-up" onClick={closeMenu}>Get Started</a>
                            </li>
                             <li class="nav-item fw-bold"><a class="btn btn-sm btn-light rounded-pill px-3 mt-2 mt-lg-0" href="/user-agreement" onClick={closeMenu} style={{background: "#014bb0","color":"white" , "border-color": "#014bb0" }}>User Agreement</a></li>
                        </ul>
                    </div>

                </div>
            </nav>

            <div className="agreement-container max-container py-5">
                <div className="agreement-header">
                    <h1>Quantsynx — User Agreement</h1>
                    <p className="agreement-last-updated">Last updated: [date]</p>
                </div>

                <div className="agreement-progress" aria-label="Reading progress">
                    <div className="agreement-progress-track">
                        <div className="agreement-progress-bar" style={{ width: `${progressPercent}%` }}></div>
                    </div>
                    <span className="agreement-progress-label">Reading progress: Page {page + 1} of {TOTAL_PAGES}</span>
                </div>

                <div className="agreement-body" ref={agreementBodyRef}>
                    {page === 0 && (
                        <div className="agreement-page">
                            <p>
                                This User Agreement ("Agreement") is a legal contract between you ("User", "you") and
                                [Quantsynx legal entity name], [type of entity, e.g. a company registered in [country]]
                                ("Quantsynx", "we", "us"). By creating an account, ticking "I agree", uploading any drawing, or
                                otherwise using the Quantsynx software and services ("Service"), you confirm that you have
                                read, understood and accepted this Agreement. If you do not agree, do not use the Service.
                            </p>

                            <h2>1. The Service</h2>
                            <p>
                                Quantsynx is a software tool that uses artificial intelligence to estimate construction quantities
                                from drawings uploaded by the User and to return those estimates, including in spreadsheet
                                (Excel) form. The Service is an aid for preparing estimates and is intended to reduce the time an
                                engineer spends on estimation work. It does not replace professional judgment.
                            </p>

                            <h2>1A. Scope and intended use of the Service</h2>
                            <p>
                                The Service is designed exclusively for standard residential buildings constructed as reinforced
                                cement concrete (RCC) framed structures. All RCC members are assumed to be cast in-situ only.
                                The Service is not designed for, and must not be used in connection with, precast, structural
                                steel, prefabricated, load-bearing masonry, or any other structural system.
                            </p>

                            <h3>Items covered under Free, Standard and Premium plans</h3>
                            <p>
                                The Service generates quantity and cost estimates for the following items and specifications only,
                                within the Free, Standard and Premium subscription plans:
                            </p>
                            <ul>
                                <li>(a) Foundation — isolated and combined footings only;</li>
                                <li>(b) RCC plinth beam;</li>
                                <li>(c) RCC columns;</li>
                                <li>(d) RCC floor/roof beams;</li>
                                <li>(e) RCC floor/roof slab;</li>
                                <li>(f) Old-size brickwork in walls;</li>
                                <li>(g) Cement plaster;</li>
                                <li>(h) Emulsion paint for internal and external surfaces;</li>
                                <li>(i) RCC lintel beam (treated throughout as floor beams for estimation purposes);</li>
                                <li>(j) RCC staircase;</li>
                                <li>(k) Tile flooring;</li>
                                <li>(l) GI/CPVC/UPVC/CP plumbing fittings (standard residential specification); and</li>
                                <li>(m) Electrical supply as per standard norms for small residential buildings.</li>
                            </ul>
                            <p>
                                All items and specifications are assumed to follow standard residential construction practice
                                prevailing in India at the time of use. The Service does not account for special or non-standard
                                specifications, site-specific conditions, local authority requirements, or any item not listed above.
                            </p>

                            <h3>Enterprise plan</h3>
                            <p>
                                Any requirement outside the scope described above — including different structural systems,
                                non-standard items, specialist specifications, or commercial/industrial buildings — is not covered
                                under the Free, Standard or Premium plans and shall be made available only through the Enterprise
                                plan, subject to a separate written agreement. Use of the Service for any purpose outside its stated
                                scope is entirely at the User's risk.
                            </p>

                            <h2>2. Processing of uploaded drawings</h2>
                            <p>
                                You understand and agree that drawings and related files you upload are transmitted to and processed
                                by a third-party artificial intelligence provider, Google ("Gemini AI"), for the purpose of generating
                                quantity estimates. By uploading, you consent to this transmission and processing. Such processing is
                                also subject to the third-party provider's own terms and policies, over which Quantsynx has no control.
                            </p>

                            <h2>3. Confidentiality and non-disclosure</h2>
                            <p>
                                Quantsynx will not disclose, sell or share your uploaded drawings with any outside party other than the
                                third-party AI provider named in Clause 2, except where required by law or with your consent. We apply
                                reasonable measures to protect your data but cannot guarantee absolute security of transmission or storage.
                            </p>

                            <h2>4. Your rights and responsibilities regarding uploads</h2>
                            <p>
                                You represent and warrant that you own, or are authorised to use and upload, every drawing and file you
                                submit, and that uploading it and having it processed as described does not breach any third party's
                                rights or any confidentiality obligation you owe. You are solely responsible for obtaining any consents
                                required from your own clients or employers before uploading.
                            </p>
                        </div>
                    )}

                    {page === 1 && (
                        <div className="agreement-page">
                            <h2>5. Mandatory verification of results</h2>
                            <p>
                                All output generated by the Service, including any downloaded Excel file, must be independently checked
                                and corrected for accuracy by a qualified engineer or manager on your side before any use. The output is a
                                starting point, not a verified estimate.
                            </p>

                            <h2>6. AI-based output — no reliance for commercial decisions</h2>
                            <p>
                                You acknowledge that results are produced by AI prediction and may contain errors, omissions or inaccuracies.
                                The output must not be applied directly to any commercial activity — including but not limited to quoting a
                                tender, pricing a bid, accepting a contract, or any binding commitment — without independent verification and
                                professional review by you.
                            </p>

                            <h2>7. Final approval rests with the User</h2>
                            <p>
                                The responsibility for the final approval, correctness and use of any estimate produced with the Service
                                lies entirely with you, the User, and not with Quantsynx.
                            </p>

                            <h2>8. No warranty</h2>
                            <p>
                                The Service is provided "as is" and "as available", without warranties of any kind, whether express or implied,
                                including any implied warranties of accuracy, merchantability, fitness for a particular purpose, or
                                non-infringement. We do not warrant that the Service will be uninterrupted, error-free, or that any estimate
                                will be accurate or complete.
                            </p>

                            <h2>9. Limitation of liability</h2>
                            <p>
                                To the maximum extent permitted by law, Quantsynx shall not be liable for any error, inaccuracy or omission in
                                the quantities or estimates produced by the Service, nor for any direct, indirect, incidental, special,
                                consequential or punitive loss or damage, including loss of profit, loss of contract, cost overruns, or losses
                                arising from reliance on the output. Where a cap applies, total aggregate liability shall not exceed the fees
                                paid for the Service in the twelve (12) months preceding the claim.
                            </p>

                            <h2>10. Indemnity</h2>
                            <p>
                                You agree to indemnify and hold harmless Quantsynx against any claim, loss or expense arising from your use of
                                the Service, your breach of this Agreement, or your use of any output in breach of Clauses 5 to 7.
                            </p>

                            <h2>11. Intellectual property</h2>
                            <p>
                                You retain ownership of the drawings you upload. You grant Quantsynx a limited licence to process them solely to
                                provide the Service as described. Quantsynx retains all rights in the Service software itself.
                            </p>

                            <h2>12. Changes to this Agreement</h2>
                            <p>
                                We may update this Agreement from time to time. Continued use of the Service after an update constitutes
                                acceptance of the revised terms.
                            </p>

                            <h2>13. Governing law and jurisdiction</h2>
                            <p>
                                This Agreement is governed by the laws of [jurisdiction/country], and the courts of [location] shall have
                                exclusive jurisdiction over any dispute.
                            </p>
                        </div>
                    )}

                    {page === 2 && (
                        <div className="agreement-page">
                            <h2>15. Scope limitations and excluded risks</h2>
                            <p>
                                The Service estimates are based solely on the items and structural type listed in Clause 1A. The following
                                are entirely outside the scope of the Service, and Quantsynx accepts no liability whatsoever in connection
                                with them:
                            </p>
                            <ul>
                                <li>(a) Any item, trade, or specification not listed in Clause 1A, including structural steel, precast elements, waterproofing, external development, landscaping, lifts, HVAC, fire fighting, solar, or any finishing item beyond those listed;</li>
                                <li>(b) Variations in material rates due to market fluctuations, inflation, regional price differences, or timing;</li>
                                <li>(c) Site conditions, soil type, bearing capacity, or geotechnical factors that may affect foundation design or quantities;</li>
                                <li>(d) Variations between drawing dimensions and actual constructed dimensions;</li>
                                <li>(e) Design changes, revisions or variations issued after the drawings were uploaded;</li>
                                <li>(f) Errors, omissions or ambiguities in the drawings uploaded by the User;</li>
                                <li>(g) Contractor's labour rates, overhead, profit margins, or any cost elements beyond material quantities;</li>
                                <li>(h) Compliance with local building codes, structural codes, or any statutory requirement;</li>
                                <li>(i) Any loss, claim, cost overrun, dispute, or damage of any kind arising from the use of the output in a tender, bid, contract, client quotation, or any commercial or legal proceeding.</li>
                            </ul>

                            <h2>16. Drawing quality and AI interpretation limitations</h2>
                            <p>
                                The accuracy of the output depends directly on the quality, completeness, scale accuracy and legibility of
                                the drawings. The AI may misread, misinterpret, or fail to detect dimensions, member sizes, or elements in
                                drawings that are unclear, incomplete, hand-drawn, or not in standard civil/structural drawing format.
                                Quantsynx gives no assurance that the AI will correctly interpret any particular drawing and accepts no
                                liability for any inaccuracy or error arising from drawing quality or AI misinterpretation.
                            </p>

                            <h2>17. No professional engineering service</h2>
                            <p>
                                The Service does not constitute the provision of professional engineering, quantity surveying, or
                                architectural services. No engineer-client relationship is created between you and Quantsynx. The output is
                                a machine-drafted draft estimate only. All professional responsibilities — including structural adequacy,
                                code compliance, cost management, and project delivery — remain with the qualified professionals engaged by you.
                            </p>

                            <h2>18. Subscription fee not indicative of project value or risk</h2>
                            <p>
                                The subscription fee bears no relationship to the value of any project, contract, or transaction in which you
                                may use the output. It is not a measure of, and does not in any way expand or justify, any claim against
                                Quantsynx. Our total aggregate liability, however arising, shall not exceed the total fees actually paid by
                                you for the Service in the twelve (12) calendar months immediately preceding the event giving rise to the claim.
                            </p>

                            <h2>19. Acknowledgement of known limitations before use</h2>
                            <p>Before using the Service, you confirm that you have been made aware of, and accept, all of the following limitations:</p>
                            <ul>
                                <li>(a) AI-generated estimates are inherently probabilistic and may deviate from actual quantities;</li>
                                <li>(b) The Service does not independently verify the drawings or structural design provided;</li>
                                <li>(c) No two projects are identical; standard assumptions embedded in the Service may not apply to your project;</li>
                                <li>(d) The output must be reviewed, corrected, and approved by a qualified professional before any commercial use; and</li>
                                <li>(e) Quantsynx is a software tool and its output carries no professional certification or guarantee of any kind.</li>
                            </ul>

                            <h2>20. Causation and contributory negligence</h2>
                            <p>
                                Any loss, cost overrun, or damage alleged by you must be shown to have been caused solely and directly by a
                                specific, identifiable error in the Service's output, not by another factor. Where any loss arises from a
                                combination of causes, Quantsynx's contribution (if any) shall be apportioned accordingly, and you shall bear
                                the portion attributable to your own failure to comply with this Agreement.
                            </p>

                            <h2>21. Dispute resolution and pre-litigation notice</h2>
                            <p>
                                Before any legal proceedings, arbitration, or formal claim, the User must give written notice to Quantsynx
                                describing the alleged error, the output relied upon, the nature of the loss, and the quantum claimed.
                                Quantsynx shall have thirty (30) days from receipt to respond. No claim shall be brought more than six (6)
                                months after the date the User became aware of the event giving rise to the claim.
                            </p>

                            <h2>22. Severability and entire agreement</h2>
                            <p>
                                If any provision is held invalid or unenforceable, the remaining provisions shall continue in full force. This
                                Agreement, together with any plan-specific terms, constitutes the entire agreement between you and
                                Quantsynx regarding the Service and supersedes all prior representations or agreements.
                            </p>

                            <h2>14. Contact</h2>
                            <p>
                                Questions about this Agreement may be sent to [contact email / address]. By proceeding, you confirm that
                                you have read and agree to this User Agreement.
                            </p>
                        </div>
                    )}

                    <div className="agreement-pagination">
                        <button
                            className="agreement-page-btn agreement-page-btn-back"
                            onClick={handleBack}
                            disabled={page === 0}
                        >
                            ← Back
                        </button>

                        <span className="agreement-page-indicator">
                            Page {page + 1} of {TOTAL_PAGES}
                        </span>

                        {page < TOTAL_PAGES - 1 ? (
                            <button
                                className="agreement-page-btn agreement-page-btn-next"
                                onClick={handleNext}
                            >
                                Next →
                            </button>
                        ) : (
                            <button
                                className="agreement-page-btn agreement-page-btn-next"
                                onClick={handleNext}
                            >
                                Back to Top ↑
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="d-flex flex-column max-container gap-5 mb-5 mt-5">
                <div className="d-flex">
                    <div className="d-flex flex-column w-50">
                        <img className="loader-img" src={APP_LOGO_PATH} alt="Logo" />
                        <span className="mt-2">CivilVertex AI is your complete construction management solution — from client onboarding and 
                            AI-powered estimation to progress tracking and invoice generation.</span>
                    </div>
                    <div style={{ width: "25%" }}>
                        <h5 className="fw-bold">Features</h5>
                        <div className="d-flex flex-column gap-2 mt-3">
                            <span>Client Management</span>
                            <span>AI Estimation</span>
                            <span>Progress Tracking</span>
                            <span>Invoice Generation</span>
                        </div>
                    </div>
                    <div style={{ width: "25%" }}>
                        <h5 className="fw-bold">Resources</h5>
                        <div className="d-flex flex-column gap-2 mt-3">
                            <span><a className="text-decoration-none" href="/user-agreement">User Agreement</a></span>
                            <span>Documentation</span>
                            <span>API Reference</span>
                            <span>Support</span>
                            <span>Contact Us</span>
                            
                        </div>
                    </div>
                </div>
                <hr />
                <div className="fw-bold text-center">
                    © 2026 CivilVertex AI. AI-powered construction management.
                </div>
            </div>
        </div>
    )
}
